import { useEffect, useMemo, useState } from 'react';
import { FORMAS_PAGAMENTO } from '@/features/financeiro/api/financeiroApi';
import { Field } from '@/shared/ui/Field';
import { SearchInput } from '@/shared/ui/SearchInput';
import { pdvApi, type CaixaResumo, type VendaDetalhe, type VendaResumo } from '../api/pdvApi';
import { formatarDataHora, formatarDinheiro, textoDoErro } from '../formato';

type Props = {
  caixa: CaixaResumo;
  /** Venda já escolhida (veio do histórico). */
  vendaInicial?: string | null;
  onConcluida: (info: { valor: number; numero: string }) => void;
  onTrocar: (credito: number) => void;
  onVoltar: () => void;
};

/**
 * Devolução parcial.
 *
 * Não existe operação de "troca" no modelo: trocar é devolver e vender de
 * novo, encadeados. Contabilmente já é isso, e encadear reusa estoque,
 * caixa e sync sem inventar um terceiro tipo de documento para manter.
 *
 * A devolução entra no caixa aberto de hoje, mesmo que a venda seja de
 * ontem: o caixa daquele dia já virou lançamento no financeiro, e
 * reescrever histórico lançado é o tipo de porta que vira fraude.
 */
export function PdvDevolucao({ caixa, vendaInicial, onConcluida, onTrocar, onVoltar }: Props) {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<VendaResumo[]>([]);
  const [venda, setVenda] = useState<VendaDetalhe | null>(null);
  const [quantidades, setQuantidades] = useState<Map<string, number>>(new Map());
  const [formaPagamento, setFormaPagamento] = useState('Dinheiro');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    if (!vendaInicial) return;
    let vivo = true;
    pdvApi
      .obterVenda(vendaInicial)
      .then((v) => {
        if (vivo) setVenda(v);
      })
      .catch((e) => {
        if (vivo) setErro(textoDoErro(e));
      });
    return () => {
      vivo = false;
    };
  }, [vendaInicial]);

  useEffect(() => {
    if (venda) return;
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      return;
    }
    const t = setTimeout(() => {
      setBuscando(true);
      pdvApi
        .listarVendas({ busca: termo })
        .then((lista) => setResultados(lista.filter((v) => !v.vendaOrigemId)))
        .catch((e) => setErro(textoDoErro(e)))
        .finally(() => setBuscando(false));
    }, 250);
    return () => clearTimeout(t);
  }, [busca, venda]);

  const total = useMemo(() => {
    if (!venda) return 0;
    let soma = 0;
    for (const item of venda.itens) {
      const qtd = quantidades.get(item.id) ?? 0;
      if (qtd <= 0) continue;
      // Devolve o valor líquido: o desconto dado na venda vale na volta.
      soma += (item.subtotal / item.quantidade) * qtd;
    }
    return Math.round(soma * 100) / 100;
  }, [venda, quantidades]);

  /** Grava a devolução. Devolve o valor efetivado, ou null se falhou. */
  async function executarDevolucao(): Promise<number | null> {
    if (!venda || total <= 0) return null;
    setErro(null);
    setSalvando(true);
    try {
      const itens = [...quantidades.entries()]
        .filter(([, q]) => q > 0)
        .map(([itemId, quantidade]) => ({ itemId, quantidade }));
      const r = await pdvApi.devolver(venda.id, {
        id: crypto.randomUUID(),
        caixaId: caixa.id,
        itens,
        formaPagamento,
        motivo: motivo.trim() || null,
      });
      onConcluida({ valor: r.valor, numero: r.numero });
      return r.valor;
    } catch (err) {
      setErro(textoDoErro(err));
      return null;
    } finally {
      setSalvando(false);
    }
  }

  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    await executarDevolucao();
  }

  if (!venda) {
    return (
      <div className="pdv-historico">
        <div className="pdv-historico__filtros">
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Número da venda no cupom do cliente"
            ariaLabel="Buscar venda para devolver"
          />
          <button type="button" className="btn btn--ghost" onClick={onVoltar}>
            Voltar
          </button>
        </div>

        <p className="pdv-cartao__ajuda">
          Metade dos clientes chega sem o cupom — nesse caso, ache a venda pelo histórico e abra a
          devolução por lá.
        </p>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        {buscando && <p className="pdv-centro--vazio">Procurando…</p>}

        {resultados.length > 0 && (
          <ul className="pdv-historico__lista">
            {resultados.map((v) => (
              <li key={v.id} className="pdv-historico__item">
                <div className="pdv-historico__identidade">
                  <span className="pdv-historico__numero">{v.numero}</span>
                  <span className="pdv-historico__cliente">{v.clienteNome}</span>
                </div>
                <div className="pdv-historico__meta">
                  <span>{formatarDataHora(v.data)}</span>
                  <span>{v.quantidadeItens} item(s)</span>
                </div>
                <strong className="pdv-historico__valor">{formatarDinheiro(v.valorTotal)}</strong>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void pdvApi.obterVenda(v.id).then(setVenda).catch((e) => setErro(textoDoErro(e)))}
                >
                  Devolver
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const nadaADevolver = venda.itens.every((i) => i.quantidade - i.quantidadeDevolvida <= 0);

  return (
    <div className="pdv-centro">
      <form className="pdv-cartao pdv-cartao--largo" onSubmit={confirmar}>
        <h2 className="pdv-cartao__titulo">Devolver da venda {venda.numero}</h2>
        <p className="pdv-cartao__ajuda">
          {formatarDataHora(venda.data)} · {venda.clienteNome} ·{' '}
          {formatarDinheiro(venda.valorTotal)}
        </p>

        {nadaADevolver && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            Todos os itens desta venda já foram devolvidos.
          </p>
        )}

        <ul className="pdv-devolucao__itens">
          {venda.itens.map((item) => {
            const disponivel = item.quantidade - item.quantidadeDevolvida;
            const escolhida = quantidades.get(item.id) ?? 0;
            const unitario = item.subtotal / item.quantidade;
            return (
              <li key={item.id} className="pdv-devolucao__item">
                <div className="pdv-devolucao__identidade">
                  <span className="pdv-item__descricao">{item.descricao}</span>
                  <span className="pdv-item__codigo">
                    {item.quantidade} vendida(s) · {formatarDinheiro(unitario)} cada
                    {item.quantidadeDevolvida > 0 && ` · ${item.quantidadeDevolvida} já devolvida(s)`}
                  </span>
                </div>

                {disponivel > 0 ? (
                  <div className="pdv-devolucao__controle">
                    <input
                      className="input pdv-num pdv-num--qtd"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={disponivel}
                      step={1}
                      value={escolhida}
                      aria-label={`Quantidade a devolver de ${item.descricao}`}
                      onChange={(e) => {
                        const q = Math.min(
                          disponivel,
                          Math.max(0, Math.floor(Number(e.target.value) || 0)),
                        );
                        setQuantidades((atual) => new Map(atual).set(item.id, q));
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() =>
                        setQuantidades((atual) => new Map(atual).set(item.id, disponivel))
                      }
                    >
                      Tudo ({disponivel})
                    </button>
                  </div>
                ) : (
                  <span className="pdv-devolucao__esgotado">Já devolvido</span>
                )}

                <strong className="pdv-devolucao__valor">
                  {escolhida > 0 ? formatarDinheiro(unitario * escolhida) : '—'}
                </strong>
              </li>
            );
          })}
        </ul>

        <Field rotulo="Como o valor volta ao cliente">
          {(id) => (
            <select
              id={id}
              className="input"
              value={formaPagamento}
              onChange={(e) => setFormaPagamento(e.target.value)}
            >
              {FORMAS_PAGAMENTO.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field rotulo="Motivo" dica="Opcional, mas ajuda a entender o padrão de devoluções depois.">
          {(id) => (
            <input
              id={id}
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: peça errada para o modelo do carro."
            />
          )}
        </Field>

        <div className="pdv-total">
          <span className="pdv-total__rotulo">A devolver</span>
          <strong className="pdv-total__valor">{formatarDinheiro(total)}</strong>
          {formaPagamento === 'Dinheiro' && total > 0 && (
            <span className="pdv-total__desconto">Sai da gaveta do caixa aberto.</span>
          )}
        </div>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setVenda(null);
              setQuantidades(new Map());
              setBusca('');
            }}
          >
            Trocar de venda
          </button>
          {total > 0 && (
            <button
              type="button"
              className="btn"
              disabled={salvando}
              onClick={async () => {
                // Troca é devolução seguida de venda — só encadeia se a
                // devolução tiver de fato acontecido.
                const devolvido = await executarDevolucao();
                if (devolvido != null) onTrocar(devolvido);
              }}
            >
              Devolver e levar outra peça
            </button>
          )}
          <button type="submit" className="btn btn--primary" disabled={salvando || total <= 0}>
            {salvando ? 'Devolvendo…' : `Devolver ${formatarDinheiro(total)}`}
          </button>
        </div>
      </form>
    </div>
  );
}
